import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BlueprintsArrayObjectTypes } from "@common/types/blueprint-types";
import {
  updateBlueprintModalContentShown,
  updateSelectedBlueprint,
  updateShowSaveBlueprintModal,
  updateUpdatedBlueprintInfo,
} from "@src/reducers/your-reducer";
import { RootState } from "@src/reducers/store";
import { findMatchingBlueprintsByName } from "../../helpers/find-matching-blueprints-by-name";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { getAuthor } from "@src/cloud-connect/blueprint/get-author";
import { newSaveBlueprintUseCase } from "@src/cloud-connect/blueprint/use-cases/new-save-blueprint-use-case";
import { getBlueprintsLibraryUseCase } from "../get-blueprints-library-use-case";
import { useI18N } from "@src/language/language-context";

export const useSaveContentHook = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const popupRef = useRef<HTMLDivElement>(null);

  const selectedBlueprint = useSelector(
    (state: RootState) => state.yourSlice.saveBlueprintModal.selectedBlueprint,
  );

  const [availableBlueprintsArray, setAvailableBlueprintsArray] = useState<
    BlueprintsArrayObjectTypes[]
  >([]);

  const [matchingBlueprintsArray, setMatchingBlueprintsArray] = useState<
    BlueprintsArrayObjectTypes[]
  >([]);

  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [textareaValue, setTextareaValue] = useState("");
  const [hasUserSelectedBlueprint, setHasUserSelectedBlueprint] =
    useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [activePopover, setActivePopover] =
    useState<BlueprintsArrayObjectTypes>({
      id: "",
      name: "",
      created_at: "",
      author: "",
      organization: "",
      description: "",
      verified: false,
      updated_at: null,
    });

  const texts = {
    successMessage: i18n("modules.saveBlueprintModal.successMessage"),
    errorMessage: i18n("modules.saveBlueprintModal.errorMessage"),
    errorNodeStructureForBlueprintTooManyNodes: i18n(
      "modules.saveBlueprintModal.errorNodeStructureForBlueprintTooManyNodes",
    ),
    subTitle: i18n("modules.saveBlueprintModal.subTitle"),
    saveButton: i18n("modules.saveBlueprintModal.saveButton"),
    cancelButton: i18n("modules.saveBlueprintModal.cancelButton"),
  };

  const closeModal = () => {
    dispatch(updateShowSaveBlueprintModal(false));
    dispatch(updateSelectedBlueprint(undefined));
    setMatchingBlueprintsArray([]);
    setInputValue("");
    setTextareaValue("");
    setDisableSaveButton(false);
  };

  const handleSelectBlueprint = (blueprint) => {
    setHasUserSelectedBlueprint(true);
    dispatch(updateSelectedBlueprint(blueprint));
    setInputValue(blueprint.name);
    setTextareaValue(blueprint.description);
    setMatchingBlueprintsArray([]);
  };

  const handleInputChange = (value) => {
    setInputValue(value);
    setHasUserSelectedBlueprint(false);
  };

  const handleTextareaChange = (value) => {
    setTextareaValue(value);
    setHasUserSelectedBlueprint(false);
  };

  const checkIfSaveButtonShouldBeDisabled = () => {
    return inputValue.length === 0 || disableSaveButton;
  };

  const setButtonVariantToInactiveIfDisabled = () => {
    return checkIfSaveButtonShouldBeDisabled() ? "inactive" : "";
  };

  const showReplaceBlueprintModal = () => {
    dispatch(
      updateUpdatedBlueprintInfo({
        name: inputValue,
        description: textareaValue,
        author: getAuthor(),
      }),
    );
    dispatch(updateBlueprintModalContentShown("replace"));
  };

  const handleSaveBlueprintResponse = (response) => {
    if (response && !response.error) {
      successSnackbar(texts.successMessage, 5000);
      closeModal();
    } else if (
      response &&
      response.error &&
      response.statusCode === 422 &&
      response.message.includes("50000")
    ) {
      errorSnackbar(texts.errorNodeStructureForBlueprintTooManyNodes, 5000);
      closeModal();
    } else {
      errorSnackbar(texts.errorMessage, 5000);
      closeModal();
    }
  };

  const saveSelectedBlueprint = async () => {
    if (!selectedBlueprint && inputValue) {
      const response = await newSaveBlueprintUseCase(inputValue, textareaValue);

      handleSaveBlueprintResponse(response);
    }
  };

  const handleSaveBlueprintButtonClick = () => {
    setDisableSaveButton(true);
    selectedBlueprint ? showReplaceBlueprintModal() : saveSelectedBlueprint();
  };

  const loadAvailableBlueprints = async () => {
    if (!authContext) {
      setAvailableBlueprintsArray([]);
    }

    setAvailableBlueprintsArray(await getBlueprintsLibraryUseCase());
  };

  const findAndSetMatchingBlueprintsArray = () => {
    setMatchingBlueprintsArray(
      findMatchingBlueprintsByName(availableBlueprintsArray, inputValue),
    );
  };

  const handleClickOnModal = () => {
    setMatchingBlueprintsArray([]);
  };

  useEffect(() => {
    loadAvailableBlueprints();
    if (selectedBlueprint) {
      setInputValue(selectedBlueprint.name);
      setTextareaValue(selectedBlueprint.description);
    }
  }, []);

  // runs everytime the input field changes, because we do not have a submit button
  useEffect(() => {
    if (!hasUserSelectedBlueprint) {
      findAndSetMatchingBlueprintsArray();
    }
  }, [inputValue]);

  return {
    popupRef,
    matchingBlueprintsArray,
    popoverPosition,
    activePopover,
    selectedBlueprint,
    inputValue,
    textareaValue,
    texts,
    setPopoverPosition,
    setActivePopover,
    closeModal,
    handleSaveBlueprintButtonClick,
    checkIfSaveButtonShouldBeDisabled,
    handleSelectBlueprint,
    handleInputChange,
    handleTextareaChange,
    setButtonVariantToInactiveIfDisabled,
    handleClickOnModal,
  };
};
